// Web Push opt-in, progressively enhanced. Two jobs that only look like one:
//
//   * Register the service worker for EVERY visitor. It is what makes the app
//     installable and the offline fallback work, so it must not wait for
//     anyone to want notifications.
//   * Enhance the server-rendered [data-push-alerts] panel with the browser's
//     Push API. The panel ships hidden and is revealed only when this browser
//     can actually do push -- the server renders it only when a VAPID key is
//     configured, so there is nothing to reveal in an app without one.
//
// No island: the panel is plain markup wired by data attributes, the same way
// passkey.js enhances plain forms.

const SUPPORTED =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window

const MESSAGES = {
  on: "Notifications are on for this browser.",
  off: "Get a notification on this device when something needs you.",
  blocked: "Notifications are blocked in this browser's settings.",
  working: "Working…"
}

let registration = null

function metaContent(name) {
  return document.querySelector(`meta[name="${name}"]`)?.content || ""
}

// VAPID public key (base64url) -> the Uint8Array pushManager wants.
function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/")
  const raw = atob(normalized)
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
}

// One registration per page load, shared by the panel and by the plain
// installability path above.
async function ensureRegistration() {
  if (registration) return registration
  registration = await navigator.serviceWorker.register("/service-worker")
  return registration
}

async function sendSubscription(method, body) {
  const response = await fetch("/push_subscriptions", {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": metaContent("csrf-token")
    },
    body: JSON.stringify(body)
  })
  if (!response.ok) throw new Error(`push_subscriptions responded ${response.status}`)
}

function render(panel, state) {
  const status = panel.querySelector("[data-push-status]")
  const enable = panel.querySelector("[data-push-enable]")
  const disable = panel.querySelector("[data-push-disable]")

  if (status) status.textContent = MESSAGES[state] || MESSAGES.off
  if (enable) enable.hidden = state !== "off"
  if (disable) disable.hidden = state !== "on"
}

function toast(type, message) {
  document.dispatchEvent(new CustomEvent("toast", { detail: { type, message } }))
}

async function enable(panel) {
  render(panel, "working")

  const permission = await Notification.requestPermission()
  if (permission !== "granted") {
    render(panel, permission === "denied" ? "blocked" : "off")
    return
  }

  const key = metaContent("vapid-public-key")
  if (!key) {
    render(panel, "off")
    return
  }

  const reg = await ensureRegistration()
  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key)
  })
  await sendSubscription("POST", subscription.toJSON())
  render(panel, "on")
}

async function disable(panel) {
  render(panel, "working")

  const reg = await ensureRegistration()
  const subscription = await reg.pushManager.getSubscription()
  if (subscription) {
    await sendSubscription("DELETE", { endpoint: subscription.endpoint })
    await subscription.unsubscribe()
  }
  render(panel, "off")
}

// Reveals the panel and shows the state this browser is actually in. Safe to
// run repeatedly: a Turbo morph restores the server's hidden panel, so it has
// to be.
async function refreshPanel() {
  if (!SUPPORTED) return

  const panel = document.querySelector("[data-push-alerts]")
  if (!panel) return

  panel.hidden = false

  if (Notification.permission === "denied") {
    render(panel, "blocked")
    return
  }

  try {
    const reg = await ensureRegistration()
    render(panel, (await reg.pushManager.getSubscription()) ? "on" : "off")
  } catch (error) {
    render(panel, "off")
  }
}

// Every visitor gets the service worker, panel or no panel.
function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return

  ensureRegistration().catch(() => {})
}

function onClick(event) {
  const panel = event.target.closest("[data-push-alerts]")
  if (!panel) return

  if (event.target.closest("[data-push-enable]")) {
    enable(panel).catch(() => {
      render(panel, "off")
      toast("alert", "Couldn't turn notifications on.")
    })
  } else if (event.target.closest("[data-push-disable]")) {
    disable(panel).catch(() => {
      render(panel, "on")
      toast("alert", "Couldn't turn notifications off.")
    })
  }
}

// Click handling is delegated to the document, so a Turbo visit or morph that
// replaces the panel never leaves a dead or doubled-up listener behind.
export function installPushHandlers() {
  document.addEventListener("click", onClick)

  document.addEventListener("turbo:load", () => {
    registerServiceWorker()
    refreshPanel()
  })
  document.addEventListener("turbo:morph", refreshPanel)
}
